import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import {
  EligibilityReportWritebackModal,
  SaveEligibilityReportProvider,
} from '@frontend/insurance-verification-reports-writeback';
import { theme } from '@frontend/theme';
import { ModalControlResponse, SearchField, SwitchChipGroup, useControlledField } from '@frontend/design-system';
import { insuranceReportTrackingIds } from '../../../constants';
import { NETWORK_KEY_MAPPINGS_BY_VENDOR } from '../../../helpers/formattedDentalCoverageTableData';
import { useInsuranceEligibilityReportContainerContext } from '../../../providers/insurance-report-container.provider';
import { InsuranceVerificationReportHeader } from '../../../shared/components/InsuranceVerificationReportHeader';
import { reportInfoContainerStyles } from '../../../styles';
import { DentalInsuranceDetailsContainer } from '../components/dental-insurance-details-container';
import { AccordionInsuranceTables } from '../components/index';
import { InsuranceSectionFilterContainer } from '../components/InsuranceTables/InsuranceSectionFilterContainer';
import { PrintableInsuranceReport } from '../components/PrintableInsuranceReport';
import { useDentalInsuranceReportContext } from '../context/DentalInsuranceReportDetailsContext.provider';
import { Vendors } from '../types/types';
import { PrintReport } from './PrintReport';

type InsuranceEligibilityReportProps = {
  saveEligibilityReportModalProps: ModalControlResponse;
  closeInsuranceReportModal: () => void;
};

export const InsuranceEligibilityReport = ({
  saveEligibilityReportModalProps,
  closeInsuranceReportModal,
}: InsuranceEligibilityReportProps) => {
  const DEBOUNCE_TIME_IN_MILLISECS = 500;
  const { t } = useTranslation();
  const {
    handlePrint,
    handleSaveToPms,
    sourceId,
    locationId,
    personPMSId,
    integrationDetails: { PMSName },
  } = useInsuranceEligibilityReportContainerContext();
  const { isPrintDialogVisible, vendor, updateTablesData } = useDentalInsuranceReportContext();

  const [switchValue, setSwitchValue] = useState<string>(
    NETWORK_KEY_MAPPINGS_BY_VENDOR[vendor as Vendors]['in-network']
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedUpdateTables = useCallback((searchTerm: string) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      updateTablesData({ searchTerm });
    }, DEBOUNCE_TIME_IN_MILLISECS);
  }, []);

  const searchFieldProps = useControlledField({
    type: 'text',
    onChange: (value: string) => {
      setSearchTerm(value);
      debouncedUpdateTables(value);
    },
    value: searchTerm,
  });

  /*
    This use effect helps in updating the default value when vendor changes

    Initially, before the customization flag loads, the default vendor is DentalXChange,
    and the network key is set accordingly.

    Once the customization flag loads and the vendor updates, we need to update the
    selected switch value based on the new vendor.

    Since the use-switch-chip-group component from the design-system does not support
    dynamically updating the default selected value, this useEffect manually sets the
    switch value whenever the vendor changes.

    */
  useEffect(() => {
    setSwitchValue(NETWORK_KEY_MAPPINGS_BY_VENDOR[vendor as Vendors]['in-network']);
  }, [vendor]);

  useEffect(() => {
    updateTablesData({ filterType: switchValue });
  }, [switchValue]);

  return (
    <SaveEligibilityReportProvider>
      <InsuranceVerificationReportHeader
        PMSName={PMSName}
        handlePrint={handlePrint}
        handleSaveToPms={handleSaveToPms}
        closeInsuranceReportModal={closeInsuranceReportModal}
      />
      <section>
        <div css={reportInfoContainerStyles}>
          <DentalInsuranceDetailsContainer />
          <InsuranceSectionFilterContainer>
            <SwitchChipGroup
              id={'global'}
              css={{ margin: theme.spacing(0.5) }}
              value={[switchValue]}
              setValue={(value) => {
                if (Array.isArray(value)) {
                  setSwitchValue(value[0]);
                }
              }}
              singleSelect={true}
            >
              <SwitchChipGroup.Option
                trackingId={insuranceReportTrackingIds.inNetworkFilterSwitchOptionBtn}
                value={NETWORK_KEY_MAPPINGS_BY_VENDOR[vendor as Vendors]['in-network']}
              >
                {t('In-Network')}
              </SwitchChipGroup.Option>
              <SwitchChipGroup.Option
                trackingId={insuranceReportTrackingIds.outOfNetworkFilterSwitchOptionBtn}
                value={NETWORK_KEY_MAPPINGS_BY_VENDOR[vendor as Vendors]['out-of-network']}
              >
                {t('Out-of-Network')}
              </SwitchChipGroup.Option>
            </SwitchChipGroup>

            <SearchField
              data-trackingid={insuranceReportTrackingIds.searchFieldTrackingId}
              css={{ width: theme.spacing(40) }}
              name={'InsuranceReportTableSearch'}
              {...searchFieldProps}
            />
          </InsuranceSectionFilterContainer>
          <AccordionInsuranceTables />
        </div>
      </section>
      {isPrintDialogVisible && <PrintReport />}
      <EligibilityReportWritebackModal
        isInsuranceVerificationReportV2Enabled={true}
        PrintableReport={<PrintableInsuranceReport />}
        PMSName={PMSName}
        locationId={locationId}
        personPMSId={personPMSId}
        sourceId={sourceId}
        {...saveEligibilityReportModalProps}
      />
    </SaveEligibilityReportProvider>
  );
};
